import { userManager } from '$lib/auth/oidc';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { handleApiError } from '$lib/utils/error/errorUtils';
import { showToast } from '$lib/helpers/toastrHelper';
import type { User } from 'oidc-client-ts';
import { writable } from 'svelte/store';

function createAuthStore() {
  const { subscribe, set: setUser } = writable<User | null>(null);
  const isLoading = writable(true);
  const isAdmin = writable(false);
  const adminEvaluated = writable(false);

  // Start the authorization-code + PKCE flow by redirecting to the identity provider.
  async function login() {
    try {
      await userManager.signinRedirect();
    } catch (error) {
      const errorMessage = handleApiError(error as Error, 'Error during login');
      showToast({
        title: 'Login Error',
        message: errorMessage,
        type: 'error'
      });
      console.error('Login error:', error);
    }
  }

  // Complete the redirect flow (called from the /callback route).
  async function completeLogin(): Promise<User | null> {
    try {
      const user = await userManager.signinRedirectCallback();
      setUser(user);
      await checkAdminStatus();
      return user;
    } catch (error) {
      const errorMessage = handleApiError(error as Error, 'Error completing login');
      showToast({
        title: 'Login Error',
        message: errorMessage,
        type: 'error'
      });
      console.error('Login callback error:', error);
      return null;
    } finally {
      isLoading.set(false);
    }
  }

  async function signOut() {
    try {
      await userManager.removeUser();
    } catch (error) {
      const errorMessage = handleApiError(error as Error, 'Error during sign out');
      showToast({
        title: 'Sign Out Error',
        message: errorMessage,
        type: 'error'
      });
      console.error('Sign out error:', error);
    } finally {
      setUser(null);
      isAdmin.set(false);
      adminEvaluated.set(true);
    }
  }

  async function checkAdminStatus() {
    const currentUser = await userManager.getUser();
    if (!currentUser || currentUser.expired) {
      isAdmin.set(false);
      adminEvaluated.set(true);
      return;
    }

    try {
      const response = await fetchWithAuth('/api/admin/check');
      if (response.ok) {
        const data = await response.json();
        if (data && data.value) {
          isAdmin.set(data.value.isAdmin);
        } else if (data && data.isAdmin !== undefined) {
          isAdmin.set(data.isAdmin);
        } else {
          isAdmin.set(false);
        }
      } else {
        isAdmin.set(false);
      }
    } catch (error) {
      const errorMessage = handleApiError(error as Error, 'Error checking admin status');
      showToast({
        title: 'Admin Check Error',
        message: errorMessage,
        type: 'error'
      });
      isAdmin.set(false);
    }

    adminEvaluated.set(true);
  }

  // Load any existing session from storage and keep the store in sync with token lifecycle events.
  async function init() {
    userManager.events.addUserLoaded((user) => setUser(user));
    userManager.events.addUserUnloaded(() => {
      setUser(null);
      isAdmin.set(false);
      adminEvaluated.set(true);
    });
    userManager.events.addAccessTokenExpired(async () => {
      // The token expired and silent refresh failed. Re-authenticate through the IDP rather than
      // dropping to the sign-in screen (which appears seamless if the SSO session is still valid).
      // This does not fire after an explicit logout, since removeUser() leaves no active token to expire.
      await login();
    });

    try {
      const user = await userManager.getUser();
      if (user) {
        if (!user.expired) {
          setUser(user);
          await checkAdminStatus();
        } else {
          // A session exists but its token has expired and silent refresh failed. Re-authenticate through
          // the IDP (which appears seamless if the SSO session is still valid). An explicit logout does NOT
          // reach here because signOut() removes the stored user, so getUser() returns null in that case
          // and we fall through to showing the sign-in button below.
          await login();
          return; // Redirecting away, keep the loading state so the sign-in screen doesn't flash
        }
      } else {
        setUser(null);
        isAdmin.set(false);
        adminEvaluated.set(true);
      }
    } catch (error) {
      console.error('Error initialising auth state:', error);
      setUser(null);
      isAdmin.set(false);
      adminEvaluated.set(true);
    }
    isLoading.set(false);
  }

  return {
    subscribe,
    login,
    completeLogin,
    signOut,
    init,
    checkAdminStatus,
    isAdmin,
    isLoading,
    adminEvaluated
  };
}

export const authStore = createAuthStore();
